import React from 'react';
import DateInput from '../../ui/DateInput';

// Schema-driven renderer for the per-stage transition forms. Sprint B uses
// this for all 6 stages; future bespoke forms (item 1.5+) can replace the
// generic field renderer with a custom component. Each field type maps to a
// matching native control.

const inputCls =
  'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-gray-600';
const labelCls = 'block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5';

function FieldLabel({ field }) {
  return (
    <span className={labelCls}>
      {field.label}
      {field.required && <span className="text-red-400/80 ml-0.5">*</span>}
    </span>
  );
}

function Field({ field, value, onChange, error, dataTestid }) {
  const id = `xfield-${field.key}`;
  const tid = dataTestid || `field-${field.key}`;
  const renderControl = () => {
    switch (field.type) {
      case 'textarea':
        return (
          <textarea id={id} data-testid={tid} rows={3} value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={inputCls} placeholder={field.placeholder || ''} />
        );
      case 'select':
        return (
          <select id={id} data-testid={tid} value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {field.options.map((opt) => (
              <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
            ))}
          </select>
        );
      case 'date':
        return (
          <DateInput id={id} data-testid={tid} value={value || ''}
            onChange={onChange} className={inputCls} />
        );
      case 'currency':
        return (
          <input id={id} data-testid={tid} type="number" inputMode="numeric" min="0"
            value={value ?? ''} onChange={(e) => onChange(e.target.value)}
            placeholder="0" className={inputCls} />
        );
      case 'number':
        return (
          <input id={id} data-testid={tid} type="number" value={value ?? ''}
            onChange={(e) => onChange(e.target.value)} className={inputCls} />
        );
      case 'checkbox':
        return (
          <label className="inline-flex items-center gap-2 text-[13px] text-gray-200">
            <input id={id} data-testid={tid} type="checkbox" checked={!!value}
              onChange={(e) => onChange(e.target.checked)} className="accent-[#B0FC38]" />
            <span>{field.label}</span>
          </label>
        );
      case 'text':
      default:
        return (
          <input id={id} data-testid={tid} type="text" value={value || ''}
            onChange={(e) => onChange(e.target.value)} className={inputCls}
            placeholder={field.placeholder || ''} />
        );
    }
  };
  return (
    <div>
      {field.type !== 'checkbox' && <label htmlFor={id}><FieldLabel field={field} /></label>}
      {renderControl()}
      {error && <p className="mt-1 text-[11px] text-red-400" data-testid={`${tid}-error`}>{error}</p>}
    </div>
  );
}

// TransitionFormFields renders the schema's fields and reports current values
// + per-field errors back to the parent. Parent owns the modal + submit
// button + onConfirm wiring.
export default function TransitionFormFields({ schema, values, onChange, errors }) {
  if (!schema?.fields) return null;
  return (
    <div className="space-y-4">
      {schema.fields.map((field) => (
        <Field
          key={field.key}
          field={field}
          value={values?.[field.key]}
          onChange={(v) => onChange({ ...values, [field.key]: v })}
          error={errors?.[field.key]}
        />
      ))}
    </div>
  );
}
